import { useState } from 'react';
import { Download, FileText, Calendar } from 'lucide-react';
import { SectionLabel, Button, LoadingSpinner, Card } from '../components/common';
import {
  GapSummaryCards,
  GapSectionChart,
  GapTrendChart,
  GapCriteriaTable
} from '../components/gaps';
import { useGapsSummary, useGapsTrends } from '../hooks/useGaps';
import { api } from '../lib/api';
import type { GapsFilters } from '../types';

function getDefaultFilters(): GapsFilters {
  const now = new Date();
  const endPeriod = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  const startDate = new Date(now);
  startDate.setMonth(startDate.getMonth() - 5);
  const startPeriod = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}`;

  return { start_period: startPeriod, end_period: endPeriod };
}

export function GapsPage() {
  const [filters, setFilters] = useState<GapsFilters>(getDefaultFilters);
  const [isExporting, setIsExporting] = useState(false);

  const { data: summary, isLoading: summaryLoading } = useGapsSummary(filters);
  const { data: trends, isLoading: trendsLoading } = useGapsTrends(filters);

  const isLoading = summaryLoading || trendsLoading;

  const handleExportCsv = () => {
    api.exportGapsCsv(filters);
  };

  const handleExportPdf = async () => {
    setIsExporting(true);
    try {
      await api.exportGapsPdf(filters);
    } finally {
      setIsExporting(false);
    }
  };

  const handleFilterChange = (key: keyof GapsFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value || undefined }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <LoadingSpinner message="Loading gap insights..." />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <SectionLabel>Analytics</SectionLabel>
          <h1 className="text-3xl font-bold text-white">Gap Insights</h1>
          <p className="text-wolf-400 mt-1">
            Track organizational weaknesses across evaluations
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={handleExportCsv}
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleExportPdf}
            disabled={isExporting}
          >
            <FileText className="w-4 h-4 mr-2" />
            {isExporting ? 'Generating...' : 'Export PDF'}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-wolf-400" />
            <span className="text-sm text-wolf-400">Period:</span>
          </div>
          <input
            type="month"
            value={filters.start_period}
            onChange={(e) => handleFilterChange('start_period', e.target.value)}
            className="bg-wolf-800 border border-wolf-700 rounded px-3 py-1.5 text-sm text-white"
          />
          <span className="text-wolf-500">to</span>
          <input
            type="month"
            value={filters.end_period}
            onChange={(e) => handleFilterChange('end_period', e.target.value)}
            className="bg-wolf-800 border border-wolf-700 rounded px-3 py-1.5 text-sm text-white"
          />
          <select
            value={filters.section_id || ''}
            onChange={(e) => handleFilterChange('section_id', e.target.value)}
            className="bg-wolf-800 border border-wolf-700 rounded px-3 py-1.5 text-sm text-white"
          >
            <option value="">All Sections</option>
            {summary?.by_section.map(s => (
              <option key={s.section_id} value={s.section_id}>
                {s.section_name}
              </option>
            ))}
          </select>
        </div>
      </Card>

      {/* Summary Cards */}
      {summary && <GapSummaryCards summary={summary} />}

      {/* Charts Row */}
      <div className="grid md:grid-cols-2 gap-6 mt-6">
        <Card>
          {trends && <GapTrendChart trends={trends} />}
        </Card>
        <Card>
          {summary && <GapSectionChart sections={summary.by_section} />}
        </Card>
      </div>

      {/* Criteria Table */}
      <Card className="mt-6">
        {summary && <GapCriteriaTable criteria={summary.top_criteria} />}
      </Card>
    </div>
  );
}
